import React, { useState, useEffect } from "react";
import { Card, Form, Button, Space, Typography, Spin, InputNumber, Empty } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT, fetchT, centsToDollar, dollarToCents } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { LargeVolumePickupConfig, UpdateLargeVolumePickupConfigRequest } from "./types";

const { Text } = Typography;

type LargeVolumePickupConfigEditorProps = {
  restaurantId: string;
};

export const LargeVolumePickupConfigEditor: React.FC<LargeVolumePickupConfigEditorProps> = ({
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { createSuccess, updateSuccess, deleteSuccess, notifyError } = useNotification();
  const [config, setConfig] = useState<LargeVolumePickupConfig | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form] = Form.useForm();

  const refreshConfig = () => {
    getFetchT<{ large_volume_pickup_config: LargeVolumePickupConfig }>(
      `${API_URL}/v1/r/${restaurantId}/admin/large_volume_pickup_config`,
      (result) => {
        const data = result.large_volume_pickup_config;
        if (data && data.restaurant_id) {
          setConfig(data);
          form.setFieldsValue({
            minimum_pretax_price: Number(centsToDollar(data.minimum_pretax_price)),
          });
        } else {
          setConfig(null);
          form.resetFields();
        }
      },
      (err) => {
        notifyError(t("failed_to_fetch_large_volume_pickup_config"), err);
        setConfig(null);
      }
    );
  };

  useEffect(() => {
    refreshConfig();
  }, [restaurantId]);

  const onFinish = (values: { minimum_pretax_price: number }) => {
    setLoading(true);

    const request: UpdateLargeVolumePickupConfigRequest = {
      minimum_pretax_price: dollarToCents(values.minimum_pretax_price),
    };

    if (config) {
      fetchT<{ large_volume_pickup_config: LargeVolumePickupConfig }>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_pickup_config`,
        request,
        () => {
          updateSuccess(t("large_volume_pickup_config"));
          setLoading(false);
          refreshConfig();
        },
        (err) => {
          notifyError(t("failed_to_update_large_volume_pickup_config"), err);
          setLoading(false);
        }
      );
    } else {
      fetchT<{ large_volume_pickup_config: LargeVolumePickupConfig }>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/large_volume_pickup_config`,
        request,
        () => {
          createSuccess(t("large_volume_pickup_config"));
          setLoading(false);
          refreshConfig();
        },
        (err) => {
          notifyError(t("failed_to_create_large_volume_pickup_config"), err);
          setLoading(false);
        }
      );
    }
  };

  const handleDelete = () => {
    setDeleting(true);
    fetch(`${API_URL}/v1/r/${restaurantId}/admin/large_volume_pickup_config`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("large_volume_pickup_config"));
        setConfig(null);
        form.resetFields();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_large_volume_pickup_config"), errorMessage);
        });
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  if (config === undefined) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("large_volume_pickup_config")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {config === null ? (
          <div>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">{t("no_large_volume_pickup_config_available")}</Text>
              }
            />
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ minimum_pretax_price: 0 }}
              style={{ marginTop: token.marginLG }}
            >
              <Form.Item
                name="minimum_pretax_price"
                label={t("minimum_pretax_price")}
                rules={[{ required: true, message: t("minimum_pretax_price_is_required") }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  prefix="$"
                  placeholder={t("enter_minimum_pretax_price")}
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t("create")}
                </Button>
              </Form.Item>
            </Form>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="minimum_pretax_price"
              label={t("minimum_pretax_price")}
              rules={[{ required: true, message: t("minimum_pretax_price_is_required") }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: "100%" }}
                prefix="$"
                placeholder={t("enter_minimum_pretax_price")}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t("save")}
                </Button>
                <Button danger onClick={handleDelete} loading={deleting}>
                  {t("delete")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};
